import { jsPDF } from 'jspdf';
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from 'docx';
import { saveAs } from 'file-saver';

export type ExportFormat = 'txt' | 'pdf' | 'docx' | 'md' | 'html';

interface ExportOptions {
  content: string;
  filename?: string;
  format: ExportFormat;
}

/**
 * Export proposal to plain text
 */
export function exportAsText(content: string, filename: string = 'proposal.txt'): void {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  saveAs(blob, filename);
}

/**
 * Export proposal to Markdown
 */
export function exportAsMarkdown(content: string, filename: string = 'proposal.md'): void {
  const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
  saveAs(blob, filename);
}

/**
 * Export proposal to HTML
 */
export function exportAsHtml(content: string, filename: string = 'proposal.html'): void {
  // Convert markdown to HTML manually (basic conversion)
  const htmlContent = markdownToHtml(content);

  const fullHtml = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Proposal</title>
  <style>
    body {
      font-family: Georgia, serif;
      line-height: 1.8;
      max-width: 800px;
      margin: 40px auto;
      padding: 20px;
      color: #333;
    }
    h1 {
      font-family: 'Plus Jakarta Sans', sans-serif;
      color: #1976d2;
      font-weight: 700;
      margin-top: 20px;
      margin-bottom: 20px;
    }
    h2 {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 600;
      margin-top: 30px;
      margin-bottom: 15px;
    }
    h3 {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 600;
      margin-top: 25px;
      margin-bottom: 12px;
    }
    p {
      margin-bottom: 15px;
    }
    ul, ol {
      margin-bottom: 15px;
      padding-left: 30px;
    }
    li {
      margin-bottom: 8px;
    }
    strong {
      font-weight: 700;
    }
    em {
      font-style: italic;
    }
    code {
      font-family: monospace;
      background-color: #f5f5f5;
      padding: 2px 4px;
      border-radius: 3px;
    }
    blockquote {
      border-left: 4px solid #1976d2;
      padding-left: 15px;
      margin-left: 0;
      font-style: italic;
      color: #666;
    }
  </style>
</head>
<body>
${htmlContent}
</body>
</html>`;

  const blob = new Blob([fullHtml], { type: 'text/html;charset=utf-8' });
  saveAs(blob, filename);
}

/**
 * Export proposal to PDF
 */
export async function exportAsPdf(content: string, filename: string = 'proposal.pdf'): Promise<void> {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 20;
  const maxWidth = pageWidth - margin * 2;
  let yPosition = margin;

  // Parse markdown and render to PDF
  const lines = content.split('\n');

  for (const line of lines) {
    if (yPosition > pageHeight - margin) {
      pdf.addPage();
      yPosition = margin;
    }

    // Handle headings
    if (line.startsWith('# ')) {
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      const text = line.replace('# ', '');
      const splitText = pdf.splitTextToSize(text, maxWidth);
      pdf.text(splitText, margin, yPosition);
      yPosition += splitText.length * 10;
    } else if (line.startsWith('## ')) {
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      const text = line.replace('## ', '');
      const splitText = pdf.splitTextToSize(text, maxWidth);
      pdf.text(splitText, margin, yPosition);
      yPosition += splitText.length * 8;
    } else if (line.startsWith('### ')) {
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      const text = line.replace('### ', '');
      const splitText = pdf.splitTextToSize(text, maxWidth);
      pdf.text(splitText, margin, yPosition);
      yPosition += splitText.length * 7;
    } else if (line.trim() === '') {
      yPosition += 5;
    } else {
      // Regular text
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      const cleanText = line.replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1');
      const splitText = pdf.splitTextToSize(cleanText, maxWidth);
      pdf.text(splitText, margin, yPosition);
      yPosition += splitText.length * 6;
    }
  }

  pdf.save(filename);
}

/**
 * Export proposal to DOCX
 */
export async function exportAsDocx(content: string, filename: string = 'proposal.docx'): Promise<void> {
  const paragraphs: Paragraph[] = [];
  const lines = content.split('\n');

  for (const line of lines) {
    if (line.startsWith('# ')) {
      // H1
      paragraphs.push(
        new Paragraph({
          text: line.replace('# ', ''),
          heading: HeadingLevel.HEADING_1,
          spacing: { before: 240, after: 120 },
        })
      );
    } else if (line.startsWith('## ')) {
      // H2
      paragraphs.push(
        new Paragraph({
          text: line.replace('## ', ''),
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 200, after: 100 },
        })
      );
    } else if (line.startsWith('### ')) {
      // H3
      paragraphs.push(
        new Paragraph({
          text: line.replace('### ', ''),
          heading: HeadingLevel.HEADING_3,
          spacing: { before: 160, after: 80 },
        })
      );
    } else if (line.trim() === '') {
      // Empty line
      paragraphs.push(new Paragraph({ text: '' }));
    } else {
      // Regular paragraph with bold/italic support
      const runs = parseMarkdownFormatting(line);
      paragraphs.push(
        new Paragraph({
          children: runs,
          spacing: { before: 0, after: 120 },
        })
      );
    }
  }

  const doc = new Document({
    sections: [
      {
        properties: {},
        children: paragraphs,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, filename);
}

/**
 * Parse markdown formatting (bold, italic) for DOCX
 */
function parseMarkdownFormatting(text: string): TextRun[] {
  const runs: TextRun[] = [];
  let currentText = '';
  let i = 0;

  while (i < text.length) {
    // Bold: **text**
    if (text.slice(i, i + 2) === '**') {
      if (currentText) {
        runs.push(new TextRun({ text: currentText }));
        currentText = '';
      }
      const endIndex = text.indexOf('**', i + 2);
      if (endIndex !== -1) {
        const boldText = text.slice(i + 2, endIndex);
        runs.push(new TextRun({ text: boldText, bold: true }));
        i = endIndex + 2;
        continue;
      }
    }

    // Italic: *text*
    if (text[i] === '*' && text[i + 1] !== '*') {
      if (currentText) {
        runs.push(new TextRun({ text: currentText }));
        currentText = '';
      }
      const endIndex = text.indexOf('*', i + 1);
      if (endIndex !== -1) {
        const italicText = text.slice(i + 1, endIndex);
        runs.push(new TextRun({ text: italicText, italics: true }));
        i = endIndex + 1;
        continue;
      }
    }

    currentText += text[i];
    i++;
  }

  if (currentText) {
    runs.push(new TextRun({ text: currentText }));
  }

  return runs.length > 0 ? runs : [new TextRun({ text })];
}

/**
 * Convert markdown to HTML (basic implementation)
 */
function markdownToHtml(markdown: string): string {
  let html = markdown;

  // Headers
  html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
  html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
  html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');

  // Bold
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

  // Italic
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');

  // Lists
  html = html.replace(/^\* (.*$)/gim, '<li>$1</li>');
  html = html.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');

  // Paragraphs
  html = html.replace(/\n\n/g, '</p><p>');
  html = '<p>' + html + '</p>';

  // Clean up
  html = html.replace(/<p><h/g, '<h');
  html = html.replace(/<\/h([1-6])><\/p>/g, '</h$1>');
  html = html.replace(/<p><ul>/g, '<ul>');
  html = html.replace(/<\/ul><\/p>/g, '</ul>');
  html = html.replace(/<p><\/p>/g, '');

  return html;
}

/**
 * Main export function
 */
export async function exportProposal(options: ExportOptions): Promise<void> {
  const { content, filename, format } = options;
  const baseFilename = filename || 'proposal';

  switch (format) {
    case 'txt':
      exportAsText(content, `${baseFilename}.txt`);
      break;
    case 'md':
      exportAsMarkdown(content, `${baseFilename}.md`);
      break;
    case 'html':
      exportAsHtml(content, `${baseFilename}.html`);
      break;
    case 'pdf':
      await exportAsPdf(content, `${baseFilename}.pdf`);
      break;
    case 'docx':
      await exportAsDocx(content, `${baseFilename}.docx`);
      break;
    default:
      throw new Error(`Unsupported export format: ${format}`);
  }
}
